import React from 'react'
import { Flex, Transition } from '@sendoutcards/quantum-design-ui'
import { useState } from 'src/hooks'
import {
  PhoneNumberInput,
  VerifyPhoneNumberFailureFragment,
  VerifyPhoneNumberFailureType,
} from 'src/graphql/generated/graphql'
import {
  useGlobalSettings,
  useMarketingContent,
  useRequestPhoneNumberVerification,
} from 'src/react_query'
import { getFormattedNumber } from 'src/onboarding/components/PhoneNumberVerification'
import { useSocPublicImageBucketUrl } from 'src/hooks/useSocPublicImageBucketUrl'
import UnverifiedAccountFound from 'src/phone_number_verification/components/UnverifiedAccountFound'
import IncorrectCode from 'src/phone_number_verification/components/IncorrectCode'
import CodeVerification from 'src/phone_number_verification/components/CodeVerification'
import PhoneNumberEntry from 'src/phone_number_verification/components/PhoneNumberEntry'
import { useRecaptcha } from 'src/hooks/useRecaptchaEnterprise'
import GenericError from './components/GenericError'

type Step =
  | { type: 'entry'; phoneNumber?: PhoneNumberInput; failureMessage?: string }
  | { type: 'verification'; phoneNumber: PhoneNumberInput }
  | {
      type: 'incorrectCode'
      phoneNumber: PhoneNumberInput
      failure: VerifyPhoneNumberFailureFragment
    }
  | {
      type: 'unverifiedAccountFound'
      phoneNumber: PhoneNumberInput
    }
  | {
      type: 'genericError'
      phoneNumber: PhoneNumberInput
      failure: VerifyPhoneNumberFailureFragment
    }

export type PhoneNumberVerificationRawProps = {
  title: string
  subtitle?: string
  validateButtonTitle?: string
  anchorButton?: {
    label: string
    action: () => void
  }
  isCreateOrLogin: boolean
  shouldDisplaySmsOptIn: boolean
  resourceType?: 'FREE_CARD' | 'GIFT_CARD' | 'OTHER'
  hero?: JSX.Element
  shouldHideCardHeroImg?: boolean
  initialPhoneNumber?: PhoneNumberInput
  /**
    The phone number that we allow the user to choose.
    This is currently used for gift cards that are already linked to a phone number and can't be redeemed by another one.
   */
  allowedPhoneNumber?: PhoneNumberInput
  verifyPhoneNumber: (
    phoneNumber: PhoneNumberInput,
    verificationCode: string,
    hasSMSOptIn?: boolean,
  ) => Promise<void | VerifyPhoneNumberFailureFragment>
  onChangeFirstAndLastName?: (firstname?: string, lastname?: string) => void
}

export const PhoneNumberVerificationRaw = (
  props: PhoneNumberVerificationRawProps,
) => {
  const {
    title,
    subtitle,
    validateButtonTitle,
    anchorButton,
    isCreateOrLogin,
    resourceType,
    hero,
    shouldHideCardHeroImg,
    shouldDisplaySmsOptIn,
    initialPhoneNumber,
    allowedPhoneNumber,
    verifyPhoneNumber,
    onChangeFirstAndLastName,
  } = props

  const { data: globalSettings } = useGlobalSettings()
  const isVerificationLevelOneEnabled =
    globalSettings?.verificationLevelOneEnabled ?? false
  const recaptcha = useRecaptcha(!isVerificationLevelOneEnabled)
  const [step, setStep] = useState<Step>({
    type: 'entry',
    phoneNumber: initialPhoneNumber,
  })
  const [formattedNumber, setFormattedNumber] = useState<string>('')
  const [hasSMSOptIn, setHasSMSOptIn] = useState<boolean | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const freeCardHeroImage = useSocPublicImageBucketUrl('freecardhero.png')

  const { data: marketingParagraphs } = useMarketingContent()

  const handleFailureMessage = (
    failureMessage: string,
    phoneNumberInput: PhoneNumberInput,
  ) => {
    setStep({
      type: 'entry',
      phoneNumber: phoneNumberInput,
      failureMessage,
    })
  }
  const requestPhoneNumberVerification = useRequestPhoneNumberVerification()

  const onRequestVerificationCode = async (
    phoneNumberInput: PhoneNumberInput,
    firstName?: string,
    lastName?: string,
    hasSMSOptIn?: boolean,
  ) => {
    if (
      allowedPhoneNumber &&
      (allowedPhoneNumber.countryIsoCode !== phoneNumberInput.countryIsoCode ||
        allowedPhoneNumber.nationalNumber !== phoneNumberInput.nationalNumber)
    ) {
      handleFailureMessage(
        marketingParagraphs?.giftCardAlreadyRedeemedDiffNumber.content ??
          'Gift card already redeemed with a different phone number.',
        phoneNumberInput,
      )
      return
    }
    try {
      onChangeFirstAndLastName?.(firstName, lastName)
      setHasSMSOptIn(hasSMSOptIn)
      setFormattedNumber(
        getFormattedNumber(
          phoneNumberInput.countryIsoCode,
          phoneNumberInput.nationalNumber,
        ),
      )
      const token =
        process.env.REACT_APP_RECAPTCHA_KEY && isVerificationLevelOneEnabled
          ? await recaptcha.getToken('verification_level_one')
          : undefined

      setIsLoading(true)
      const result = await requestPhoneNumberVerification.mutateAsync({
        phoneNumber: phoneNumberInput,
        recaptchaToken: token,
      })
      if (result.__typename === 'RequestPhoneNumberVerificationSuccess') {
        setStep({ type: 'verification', phoneNumber: phoneNumberInput })
      } else {
        handleFailureMessage(result.failureMessage, phoneNumberInput)
      }
    } catch (error) {
      handleFailureMessage(
        error?.toString() ?? 'Failed to request phone number verification.',
        phoneNumberInput,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onVerifyPhoneNumberFailure = (
    failure: VerifyPhoneNumberFailureFragment,
  ) => {
    if (step.type === 'verification') {
      if (
        [
          VerifyPhoneNumberFailureType.IncorrectCode,
          VerifyPhoneNumberFailureType.CodeExpired,
          VerifyPhoneNumberFailureType.TooManyAttempts,
        ].includes(failure.failureType)
      ) {
        setStep({
          type: 'incorrectCode',
          phoneNumber: step.phoneNumber,
          failure,
        })
      } else if (
        failure.failureType ===
        VerifyPhoneNumberFailureType.UnverifiedAccountFound
      ) {
        setStep({
          type: 'unverifiedAccountFound',
          phoneNumber: step.phoneNumber,
        })
      } else {
        setStep({
          type: 'genericError',
          phoneNumber: step.phoneNumber,
          failure,
        })
      }
    }
  }

  return (
    <>
      {isLoading && (
        <Transition
          isLoading={isLoading}
          title="Sending Code To:"
          subtitle={formattedNumber}
        />
      )}
      {!isLoading && step.type === 'entry' && (
        <Flex flexDirection={'column'} justifyContent={'center'} inset={'x1'}>
          {!shouldHideCardHeroImg &&
            (hero ? (
              hero
            ) : (
              <Flex
                flexDirection="column"
                rowGap="x0"
                justifyContent="center"
                alignItems="center"
                css={{
                  aspectRatio: '1/1',
                  background:
                    'linear-gradient(to bottom, #F6F6F600, #F6F6F600, #F6F6F600, #FFFFFF),linear-gradient(to top, #F6F6F600, #F6F6F600, #F6F6F600, #FFFFFF),linear-gradient(to right, #FFFFFF, #F6F6F600, #FFFFFF),linear-gradient(to right, #FFDFE7, #E498FD)',
                }}
                maxHeight={'30dvh'}
              >
                <div
                  style={{
                    backgroundImage: `url(${freeCardHeroImage})`,
                    backgroundPosition: 'center',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    height: '100%',
                    aspectRatio: '1/1',
                  }}
                />
              </Flex>
            ))}
          <PhoneNumberEntry
            title={title}
            subtitle={subtitle}
            validateButtonTitle={validateButtonTitle}
            requestVerificationCode={onRequestVerificationCode}
            phoneNumber={step.phoneNumber}
            failureMessage={step.failureMessage}
            anchorButton={anchorButton}
            shouldAskFirstAndLastName={!!onChangeFirstAndLastName}
            shouldDisplaySmsOptIn={shouldDisplaySmsOptIn}
            isCreateOrLogin={isCreateOrLogin}
          />
        </Flex>
      )}
      {!isLoading && step.type === 'verification' && (
        <CodeVerification
          resourceType={resourceType ?? 'OTHER'}
          phoneNumber={step.phoneNumber}
          verifyPhoneNumber={(phoneNumber, verificationCode) =>
            verifyPhoneNumber(phoneNumber, verificationCode, hasSMSOptIn)
          }
          onFailure={onVerifyPhoneNumberFailure}
          onNeedNewCode={(phoneNumber: PhoneNumberInput) => {
            setStep({
              type: 'entry',
              phoneNumber,
            })
          }}
        />
      )}
      {step.type === 'incorrectCode' && (
        <IncorrectCode
          phoneNumber={step.phoneNumber}
          getNewCode={phoneNumber =>
            setStep({ type: 'entry', phoneNumber: phoneNumber ?? undefined })
          }
          tryAgain={() =>
            setStep({ type: 'verification', phoneNumber: step.phoneNumber })
          }
          failure={step.failure}
        />
      )}
      {step.type === 'unverifiedAccountFound' && (
        <UnverifiedAccountFound phoneNumber={step.phoneNumber} />
      )}
      {step.type === 'genericError' && (
        <GenericError
          failure={step.failure}
          getNewCode={phoneNumber =>
            setStep({ type: 'entry', phoneNumber: phoneNumber ?? undefined })
          }
          tryAgain={() =>
            setStep({ type: 'verification', phoneNumber: step.phoneNumber })
          }
        />
      )}
    </>
  )
}
